import { API_BASE_URL } from '../config/api';
import { addDaysYmd, athensYmd, isYmd } from './format';

/**
 * Αρχική «Ήσυχο ταμπλό» (σχέδιο A, 13/10/2026): οι δύο κλήσεις του συμβολαίου (home + attention) και
 * όσες χρειάζονται οι παλιές αναφορές (/analytics/stores/:id, dine-in, training-summary).
 * Οι απαντήσεις «κανονικοποιούνται»: η PG στέλνει numeric ως κείμενο, και ό,τι λείπει γίνεται 0/[]/null,
 * ώστε ένα πεδίο που άργησε στο backend να μη ρίχνει τη σελίδα.
 */

export type PeriodKey = 'today' | 'yesterday' | '7d' | '30d' | 'custom';
export const PERIOD_KEYS: readonly PeriodKey[] = ['today', 'yesterday', '7d', '30d', 'custom'];

export interface PeriodSel {
  key: PeriodKey;
  /** Μόνο για custom: YYYY-MM-DD. */
  from?: string;
  to?: string;
}

export interface Kpi {
  value: number;
  delta_pct: number | null;
  series7d: number[];
}

export interface HourCum {
  hour: number;
  /** null: η ώρα δεν ήρθε ακόμη. */
  today: number | null;
  yesterday: number;
}

export interface TodaySoFar {
  until: string;
  today: { revenue: number; orders: number };
  /** null: δεν υπάρχει χθεσινή (κλειστή) περίοδος — π.χ. νέο κατάστημα. */
  yesterday: { revenue: number; orders: number } | null;
  hourly: HourCum[];
}

export interface DayRow { date: string; label: string; revenue: number; orders: number }
export interface HourRow { hour: number; revenue: number; orders: number }
export interface ChannelRow { key: string; label: string; orders: number; revenue: number; avg_basket: number; share_pct: number }
export interface PaymentRow { key: string; label: string; amount: number; share_pct: number }
export interface ProductRow {
  product_id: number | null;
  name: string;
  category_name: string | null;
  quantity: number;
  revenue: number;
  share_pct: number;
  delta_quantity: number | null;
}
export interface CategoryRow { category_id: number | null; name: string; quantity: number; revenue: number; share_pct: number }
export interface StaffRow { employee_id: number | null; name: string; orders: number; revenue: number }
/** Μία ομάδα ακυρώσεων: πόσες, πόσα €, ανά αιτία και ανά χρήστη. */
export interface CancelGroup {
  count: number;
  amount: number;
  by_reason: Array<{ reason: string; count: number }>;
  by_user: Array<{ name: string; count: number }>;
}

export interface HomeData {
  period: {
    key: PeriodKey;
    from: string | null;
    to: string | null;
    label: string;
    /**
     * Με τι συγκρίνεται. from/to null = καμία κλειστή περίοδος (όλα τα Δ null). `until` («HH:MM», μόνο στο
     * σήμερα): ως ποια ώρα μετρά το χθες (ίδια ώρα ρολογιού, τώρα − 24 ώρες).
     */
    compare: { from: string | null; to: string | null; mode: 'same-hour' | 'previous-period'; until: string | null } | null;
  };
  kpis: {
    revenue: Kpi;
    orders: Kpi;
    avg_basket: Kpi;
    cancellations: Kpi & { amount: number };
    pending: { open_tables: number; open_tables_amount: number; pending_orders: number; pending_amount: number };
  };
  today_so_far: TodaySoFar | null;
  by_day: DayRow[];
  by_hour: HourRow[];
  channels: ChannelRow[];
  payments: PaymentRow[];
  top_products: ProductRow[];
  categories: CategoryRow[];
  staff: { cashiers: StaffRow[]; waiters: StaffRow[]; drivers: StaffRow[] };
  /** Οι ακυρωμένες παραγγελίες (ό,τι μετρά ο δείκτης «Ακυρώσεις») + οι ακυρώσεις στα τραπέζια από το ημερολόγιο. */
  cancellations: CancelGroup & {
    delta_pct: number | null;
    /** Είδη τραπεζιών που ακυρώθηκαν («Διόρθωση»). */
    items: CancelGroup;
    /** Ολόκληρα τραπέζια που ακυρώθηκαν. */
    tables: CancelGroup;
  };
  training: { orders: number; amount: number };
}

export interface AttentionData {
  register: { forgotten_z: boolean; open_since: string | null; hours_open: number | null };
  printers: { offline: Array<{ id: number | string; name: string; last_seen: string | null }>; total: number };
  fiscal_failed: { count: number; oldest_at: string | null };
}

/* ───────── κανονικοποίηση ───────── */

type Raw = Record<string, any>;
const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {});
const arr = (v: unknown): Raw[] => (Array.isArray(v) ? v.map(obj) : []);
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));
const idOrNull = (v: unknown): number | null => {
  const n = numOrNull(v);
  return n === null ? null : n;
};
/** «08:00» ή 8 → 8. */
const hourOf = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? ((Math.trunc(n) % 24) + 24) % 24 : 0;
};

const kpiOf = (v: unknown): Kpi => {
  const o = obj(v);
  return { value: num(o.value), delta_pct: numOrNull(o.delta_pct), series7d: Array.isArray(o.series7d) ? o.series7d.map(num) : [] };
};

/** Λείπει (παλιό backend) → άδεια ομάδα. */
const cancelGroupOf = (v: unknown): CancelGroup => {
  const o = obj(v);
  return {
    count: num(o.count),
    amount: num(o.amount),
    by_reason: arr(o.by_reason).map(x => ({ reason: str(x.reason), count: num(x.count) })),
    by_user: arr(o.by_user).map(x => ({ name: str(x.name), count: num(x.count) })),
  };
};

const periodKeyOf = (v: unknown, fallback: PeriodKey): PeriodKey =>
  (PERIOD_KEYS as readonly string[]).includes(String(v)) ? (v as PeriodKey) : fallback;

export function normalizeHome(raw: unknown, fallbackKey: PeriodKey = 'today'): HomeData {
  const r = obj(raw);
  const p = obj(r.period);
  const cmp = p.compare ? obj(p.compare) : null;
  const k = obj(r.kpis);
  const pend = obj(k.pending);
  const tsf = r.today_so_far ? obj(r.today_so_far) : null;
  const staff = obj(r.staff);
  const canc = obj(r.cancellations);
  const staffRows = (v: unknown): StaffRow[] =>
    arr(v).map(s => ({ employee_id: idOrNull(s.employee_id), name: str(s.name), orders: num(s.orders), revenue: num(s.revenue) }));
  return {
    period: {
      key: periodKeyOf(p.key, fallbackKey),
      from: strOrNull(p.from),
      to: strOrNull(p.to),
      label: str(p.label),
      compare: cmp
        ? {
            from: strOrNull(cmp.from),
            to: strOrNull(cmp.to),
            mode: cmp.mode === 'same-hour' ? 'same-hour' : 'previous-period',
            until: typeof cmp.until === 'string' && /^\d{1,2}:\d{2}$/.test(cmp.until) ? cmp.until : null,
          }
        : null,
    },
    kpis: {
      revenue: kpiOf(k.revenue),
      orders: kpiOf(k.orders),
      avg_basket: kpiOf(k.avg_basket),
      cancellations: { ...kpiOf(k.cancellations), amount: num(obj(k.cancellations).amount) },
      pending: {
        open_tables: num(pend.open_tables),
        open_tables_amount: num(pend.open_tables_amount),
        pending_orders: num(pend.pending_orders),
        pending_amount: num(pend.pending_amount),
      },
    },
    today_so_far: tsf
      ? {
          until: str(tsf.until),
          today: { revenue: num(obj(tsf.today).revenue), orders: num(obj(tsf.today).orders) },
          yesterday: tsf.yesterday ? { revenue: num(obj(tsf.yesterday).revenue), orders: num(obj(tsf.yesterday).orders) } : null,
          hourly: arr(tsf.hourly).map(h => ({ hour: hourOf(h.hour), today: numOrNull(h.today), yesterday: num(h.yesterday) })),
        }
      : null,
    by_day: arr(r.by_day).map(d => ({ date: str(d.date).slice(0, 10), label: str(d.label), revenue: num(d.revenue), orders: num(d.orders) })),
    by_hour: arr(r.by_hour).map(h => ({ hour: hourOf(h.hour), revenue: num(h.revenue), orders: num(h.orders) })),
    channels: arr(r.channels).map(c => ({
      key: str(c.key), label: str(c.label), orders: num(c.orders), revenue: num(c.revenue),
      avg_basket: num(c.avg_basket), share_pct: num(c.share_pct),
    })),
    payments: arr(r.payments).map(pm => ({ key: str(pm.key) || 'other', label: str(pm.label), amount: num(pm.amount), share_pct: num(pm.share_pct) })),
    top_products: arr(r.top_products).map(pr => ({
      product_id: idOrNull(pr.product_id), name: str(pr.name), category_name: strOrNull(pr.category_name),
      quantity: num(pr.quantity), revenue: num(pr.revenue), share_pct: num(pr.share_pct), delta_quantity: numOrNull(pr.delta_quantity),
    })),
    categories: arr(r.categories).map(c => ({
      category_id: idOrNull(c.category_id), name: str(c.name), quantity: num(c.quantity), revenue: num(c.revenue), share_pct: num(c.share_pct),
    })),
    staff: { cashiers: staffRows(staff.cashiers), waiters: staffRows(staff.waiters), drivers: staffRows(staff.drivers) },
    cancellations: {
      ...cancelGroupOf(canc),
      delta_pct: numOrNull(canc.delta_pct),
      items: cancelGroupOf(canc.items),
      tables: cancelGroupOf(canc.tables),
    },
    training: { orders: num(obj(r.training).orders), amount: num(obj(r.training).amount) },
  };
}

export function normalizeAttention(raw: unknown): AttentionData {
  const r = obj(raw);
  const reg = obj(r.register);
  const pr = obj(r.printers);
  const ff = obj(r.fiscal_failed);
  return {
    register: { forgotten_z: reg.forgotten_z === true, open_since: strOrNull(reg.open_since), hours_open: numOrNull(reg.hours_open) },
    printers: {
      offline: arr(pr.offline).map(p => ({ id: (p.id as number | string) ?? '', name: str(p.name), last_seen: strOrNull(p.last_seen) })),
      total: num(pr.total),
    },
    fiscal_failed: { count: num(ff.count), oldest_at: strOrNull(ff.oldest_at) },
  };
}

/** Κάτι θέλει ενέργεια τώρα; (αλλιώς η λωρίδα «Προσοχή» δεν φαίνεται καθόλου) */
export const hasAttention = (a: AttentionData | null): boolean =>
  !!a && (a.register.forgotten_z || a.printers.offline.length > 0 || a.fiscal_failed.count > 0);

/* ───────── κλήσεις ───────── */

export class HomeApiError extends Error {
  status: number;
  /** Το μήνυμα του server (π.χ. 400 «Το εύρος μπορεί να είναι ως 366 ημέρες.»), όταν υπάρχει. */
  serverMessage: string | null;
  constructor(status: number, serverMessage: string | null = null) {
    super(`HTTP ${status}`);
    this.name = 'HomeApiError';
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

const authHeaders = (): Record<string, string> => ({ Authorization: `Bearer ${localStorage.getItem('xentrix_token')}` });

async function getJson(path: string, signal?: AbortSignal): Promise<unknown> {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders(), signal });
  if (!res.ok) {
    let message: string | null = null;
    try {
      const body = await res.json();
      message = body && typeof body.message === 'string' ? body.message : null;
    } catch { /* χωρίς σώμα */ }
    throw new HomeApiError(res.status, message);
  }
  return res.json();
}

/** «period=7d» / «period=custom&from=…&to=…» — και κλειδί ταυτότητας της επιλογής. */
export function periodQuery(sel: PeriodSel): string {
  const q = new URLSearchParams({ period: sel.key });
  if (sel.key === 'custom') {
    if (sel.from) q.set('from', sel.from);
    if (sel.to) q.set('to', sel.to);
  }
  return q.toString();
}

export async function fetchHome(storeId: number, sel: PeriodSel, signal?: AbortSignal): Promise<HomeData> {
  return normalizeHome(await getJson(`/api/analytics/stores/${storeId}/home?${periodQuery(sel)}`, signal), sel.key);
}

export async function fetchAttention(storeId: number, signal?: AbortSignal): Promise<AttentionData> {
  return normalizeAttention(await getJson(`/api/analytics/stores/${storeId}/attention`, signal));
}

/** Το υπάρχον GET /api/analytics/stores/:id (Εξαγωγή, Απόθεμα, ΦΠΑ & παραστατικά). */
export async function fetchLegacyAnalytics(storeId: number, from: string, to: string, signal?: AbortSignal): Promise<any> {
  return getJson(`/api/analytics/stores/${storeId}?from=${from}&to=${to}`, signal);
}

export async function fetchDineInAnalytics(storeId: number, from: string, to: string, signal?: AbortSignal): Promise<any> {
  return getJson(`/api/analytics/stores/${storeId}/dine-in?from=${from}&to=${to}`, signal);
}

export async function fetchTrainingSummary(storeId: number, from: string, to: string, signal?: AbortSignal): Promise<any> {
  return getJson(`/api/analytics/stores/${storeId}/training-summary?from=${from}&to=${to}`, signal);
}

/* ───────── ημερομηνίες περιόδου (για τις παλιές αναφορές που θέλουν from/to) ───────── */

/**
 * from/to (YYYY-MM-DD) της επιλογής για τις παλιές αναφορές, που μετρούν ημερομηνίες όπως το bizDay (όχι
 * χρονικές στιγμές): σήμερα/7/30 ημ. ημερολογιακά όπως το παλιό ταμπλό και το home (από σήμερα − ν + 1), «χθες»
 * = η ημερομηνία της τελευταίας κλειστής περιόδου (το by_day του home, αλλιώς σήμερα − 1).
 */
export function periodDates(sel: PeriodSel, home: HomeData | null, now: Date = new Date()): { from: string; to: string } {
  const today = athensYmd(now);
  switch (sel.key) {
    case 'yesterday': {
      const last = home && home.period.key === 'yesterday' ? home.by_day[home.by_day.length - 1]?.date : null;
      const y = isYmd(last) ? last : addDaysYmd(today, -1);
      return { from: y, to: y };
    }
    case '7d': return { from: addDaysYmd(today, -6), to: today };
    case '30d': return { from: addDaysYmd(today, -29), to: today };
    case 'custom': {
      const a = isYmd(sel.from) ? sel.from : today;
      const b = isYmd(sel.to) ? sel.to : a;
      return a <= b ? { from: a, to: b } : { from: b, to: a };
    }
    default: return { from: today, to: today };
  }
}
